import React, { useEffect, useRef, useState } from 'react';
import { CONNECTION_STYLE_LIST } from '../../config/connectionStyles';

function ConnectionPreview({ styleId }) {
  if (styleId === 'branch') {
    return (
      <svg aria-hidden="true" width="46" height="24" viewBox="0 0 46 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M3 12H17M17 4V20M17 4H43M17 12H43M17 20H43" />
      </svg>
    );
  }
  if (styleId === 'rounded') {
    return (
      <svg aria-hidden="true" width="46" height="24" viewBox="0 0 46 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M3 18H16Q20 18 20 14V10Q20 6 24 6H43" />
      </svg>
    );
  }
  return (
    <svg aria-hidden="true" width="46" height="24" viewBox="0 0 46 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M3 18C16 18 25 6 43 6" />
    </svg>
  );
}

const ConnectionStyleMenu = ({ connectionStyle, isDarkMode }) => {
  const menuRef = useRef(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open || typeof document === 'undefined') return undefined;
    const handlePointerDown = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) setOpen(false);
    };
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  if (!connectionStyle) return null;
  const { activeStyleId, canChangeStyle, setStyle } = connectionStyle;
  const foreground = isDarkMode ? '#e5e7eb' : '#111827';
  const buttonBackground = isDarkMode ? '#111827' : '#e5e7eb';

  return (
    <div ref={menuRef} style={{ position: 'relative' }}>
      <button
        type="button"
        aria-label="Connection style"
        aria-expanded={open}
        title={canChangeStyle
          ? 'Connection style'
          : 'Connection style (only the map owner can change it)'}
        style={{
          width: 28,
          height: 28,
          display: 'grid',
          placeItems: 'center',
          padding: 0,
          border: 0,
          borderRadius: '50%',
          background: buttonBackground,
          color: foreground,
          cursor: 'pointer',
        }}
        onClick={(event) => {
          event.stopPropagation();
          setOpen((wasOpen) => !wasOpen);
        }}
      >
        <svg
          aria-hidden="true"
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M4 12h6" />
          <path d="M10 6v12" />
          <path d="M10 6h10" />
          <path d="M10 12h10" />
          <path d="M10 18h10" />
        </svg>
      </button>
      {open && (
        <div
          role="menu"
          aria-label="Connection styles"
          style={{
            position: 'absolute',
            top: 40,
            right: 0,
            minWidth: 240,
            padding: 8,
            borderRadius: 8,
            backgroundColor: isDarkMode ? '#111827' : '#ffffff',
            boxShadow: '0 10px 15px -3px rgba(0,0,0,0.3), 0 4px 6px -2px rgba(0,0,0,0.25)',
            border: `1px solid ${isDarkMode ? '#374151' : '#e5e7eb'}`,
            zIndex: 210,
            color: foreground,
          }}
          onClick={(event) => event.stopPropagation()}
        >
          <div style={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: '0.04em',
            textTransform: 'uppercase',
            opacity: 0.65,
            padding: '2px 8px 6px',
            fontFamily: '"Quicksand", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
          }}>
            Connection style
          </div>
          {CONNECTION_STYLE_LIST.map((styleOption) => {
            const isActive = styleOption.id === activeStyleId;
            const disabled = !canChangeStyle && !isActive;
            return (
              <button
                key={styleOption.id}
                type="button"
                role="menuitemradio"
                aria-checked={isActive}
                disabled={!canChangeStyle}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '50px 1fr auto',
                  alignItems: 'center',
                  gap: 9,
                  width: '100%',
                  border: 0,
                  background: 'transparent',
                  color: 'inherit',
                  textAlign: 'left',
                  padding: '7px 8px',
                  borderRadius: 6,
                  cursor: canChangeStyle ? 'pointer' : 'default',
                  opacity: disabled ? 0.45 : 1,
                  fontFamily: '"Quicksand", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                }}
                onMouseEnter={(event) => {
                  if (canChangeStyle) {
                    event.currentTarget.style.backgroundColor = isDarkMode ? '#1f2937' : '#f3f4f6';
                  }
                }}
                onMouseLeave={(event) => {
                  event.currentTarget.style.backgroundColor = 'transparent';
                }}
                onClick={() => {
                  if (!canChangeStyle) return;
                  if (!isActive && typeof setStyle === 'function') setStyle(styleOption.id);
                  setOpen(false);
                }}
              >
                <span style={{ color: isDarkMode ? '#94a3b8' : '#64748b' }}>
                  <ConnectionPreview styleId={styleOption.id} />
                </span>
                <span>
                  <span style={{ display: 'block', fontSize: 13, fontWeight: isActive ? 700 : 500 }}>
                    {styleOption.name}
                  </span>
                  <span style={{ display: 'block', marginTop: 1, fontSize: 10.5, opacity: 0.65 }}>
                    {styleOption.description}
                  </span>
                </span>
                {isActive && <span aria-hidden="true">✓</span>}
              </button>
            );
          })}
          {!canChangeStyle && (
            <div style={{ fontSize: 11, opacity: 0.65, padding: '6px 8px 2px' }}>
              Only the map owner can change connection styles.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ConnectionStyleMenu;
